import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Store } from '@ngxs/store';
import { CertificationProvider } from 'app/library/certification-provider/models/certification-provider';
import { Certification } from 'app/library/certifications/models/certification';
import { ModuleService } from 'app/library/module/services/module.service';
import { UserService } from 'app/library/user/services/user.service';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-internal-certification-template-dialog',
    templateUrl: './internal-certification-template-dialog.component.html',
    styleUrls: ['./internal-certification-template-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class InternalCertificationTemplateDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    title: string;

    @Input()
    certificateKeywords: string[] = [];

    @Input()
    selectedProvider: CertificationProvider;

    @Input()
    certification: Certification;

    @Input()
    errors = new BehaviorSubject<{ message: string }>(null);

    @Input()
    set validity(value: boolean) {
        this.validityCtrl.setValue(value);
    }

    @Input()
    saveBtnLoading$!: BehaviorSubject<boolean>;

    @Output()
    save = new EventEmitter<{ name: string; providerId: string; validityPeriod: number; hasExpirationDate: boolean; file: File; isFileUpdated: boolean; targetCount: number }>();

    uploadedCertificateFiles: File[] = [];
    isAddingCertificate: boolean = false;
    isFileUpdated: boolean = false;
    validityCtrl = new FormControl(true);
    isEditMode: boolean = false;
    userLanguage: string;
    isDragOver = false;

    private _formGroup: FormGroup;

    get formGroup(): FormGroup {
        return this._formGroup;
    }

    constructor(
        public dialogRef: MatDialogRef<InternalCertificationTemplateDialogComponent>,
        protected _matSnackBar: MatSnackBar,
        protected _store: Store,
        protected _action$: Actions,
        protected _translateService: TranslateService,
        private _formBuilder: FormBuilder,
        public moduleService: ModuleService,
        private _userService: UserService,
    ) {
        super();

        this.userLanguage = this._userService.getMyInfo().language;
    }

    async ngOnInit() {
        this.isEditMode = !!this.certification;

        this._formGroup = this._formBuilder.group({
            name: [this.certification?.certificationName || '', Validators.required],
            validityPeriod: [this.certification?.validityPeriod || '', Validators.required],
            targetCtrl: [this.certification?.targetCount || null],
        });

        this.errors
            .asObservable()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((error) => {
                this._formGroup.controls.name.setErrors(error);
            });

        if (this.certification) {
            this.uploadedCertificateFiles[0] = this.certification.certificateTemplate as any;
        }

        this.saveBtnLoading$.pipe(takeUntil(this._unsubscribeAll)).subscribe((loading) => {
            this.isAddingCertificate = loading;
        });

        this.validityCtrl.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
            if (value) {
                this.formGroup.get('validityPeriod').setValidators([Validators.required]);
            } else {
                this.formGroup.get('validityPeriod').clearValidators();
            }
            this.formGroup.get('validityPeriod').updateValueAndValidity();
        });
    }

    formChanged(): boolean {
        if (!this.isEditMode) return false;

        return (
            this.formGroup.get('name').value !== this.certification.certificationName ||
            this.formGroup.get('targetCtrl').value !== this.certification.targetCount ||
            this.validityCtrl.value !== this.certification.validityPeriod > 0 ||
            (this.validityCtrl.value && this.formGroup.get('validityPeriod').value !== this.certification.validityPeriod) ||
            this.isFileUpdated
        );
    }

    downloadFile(): void {
        const fileName = `maxbrain_certificate_template_${this.userLanguage}`;
        const url = `https://s3.maxbrain.com/example-certificate-templates/${fileName}.docx`;
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName; // Set the file name for download
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    }

    async onAddCertificates(): Promise<void> {
        this.save.emit({
            name: this.formGroup.get('name').value,
            providerId: this.selectedProvider.id,
            validityPeriod: this.validityCtrl.value ? this.formGroup.get('validityPeriod').value : null,
            hasExpirationDate: this.validityCtrl.value,
            file: this.uploadedCertificateFiles[0],
            isFileUpdated: this.isFileUpdated,
            targetCount: this.formGroup.get('targetCtrl').value,
        });
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
    }

    onFileDropped(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;

        const fileList = event.dataTransfer?.files;
        if (fileList?.length) {
            this.uploadedCertificateFiles = Array.from(fileList);
        }
    }

    fileChange(files: File[]): void {
        // files is already a File[] from ngx-file-drag-drop
        this.uploadedCertificateFiles = files;
        this.isFileUpdated = true;
    }

    onRemoveAddedCertificate() {
        this.uploadedCertificateFiles = [];
    }

    copyKeywordToClipboard(keyword: string): void {
        const textToCopy = `<<${keyword}>>`;
        navigator.clipboard.writeText(textToCopy).then(() => {
            const successMessage = this._translateService.instant('GENERAL.MESSAGE.KEYWORD_COPIED');

            this._matSnackBar.open(successMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.fast,
            });
        });
    }
}
